import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { toast } from "sonner";

// Only allow post-login redirects to internal paths (never external URLs).
function safeRedirect(raw) {
  if (!raw) return "/dashboard";
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith("/") && !decoded.startsWith("//")) return decoded;
  } catch (_) {}
  return "/dashboard";
}

export default function Login() {
  const { login } = useAuth();
  const { tr } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirectTo = safeRedirect(params.get("redirect"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      nav(redirectTo);
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr("auth_error"));
    } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header transparent />
      <main className="flex-1 grid place-items-center px-4 py-16">
        <div className="pfi-card w-full max-w-md p-8 pfi-fade-up">
          <div className="pfi-overline">PERSONA FRAGRANCE INTELLIGENCE</div>
          <h1 className="mt-3 font-serif text-3xl">{tr("auth_signin_title")}</h1>
          <p className="mt-2 text-[#a9a9b8] text-sm">{tr("auth_signin_sub")}</p>

          <button
            onClick={google}
            data-testid="login-google-btn"
            className="mt-6 w-full pfi-btn-outline rounded-full py-3 text-sm inline-flex items-center justify-center gap-2"
          >
            {tr("auth_google")}
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-[#6c6c7a]">
            <div className="flex-1 h-px bg-white/5" />
            <span className="font-mono tracking-widest">{tr("auth_or")}</span>
            <div className="flex-1 h-px bg-white/5" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            <label className="block text-xs text-[#a9a9b8] tracking-wide">{tr("auth_email")}</label>
            <input
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg bg-[#181820] border border-white/10 px-4 py-3 text-sm text-[#f8f8fc] outline-none focus:border-[#d4af37]/60"
            />
            <label className="block text-xs text-[#a9a9b8] tracking-wide">{tr("auth_password")}</label>
            <input
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg bg-[#181820] border border-white/10 px-4 py-3 text-sm text-[#f8f8fc] outline-none focus:border-[#d4af37]/60"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="mt-2 w-full pfi-btn-gold rounded-full py-3 text-sm disabled:opacity-60"
            >
              {busy ? tr("loading") : tr("auth_signin_btn")}
            </button>
          </form>

          <div className="mt-6 text-sm text-[#a9a9b8]">
            {tr("auth_no_account")}{" "}
            <Link to="/register" data-testid="link-to-register" className="text-[#e6c687] hover:text-[#f8f8fc]">
              {tr("auth_signup_btn")}
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
